import { useState, useEffect, useCallback } from "react";
import { useDispatch } from "react-redux";
import { useTranslation } from "react-i18next";
import {
  Box, Checkbox, Chip, CircularProgress, FormControl, FormControlLabel,
  IconButton, InputLabel, MenuItem, Select, Tab, Tabs, Tooltip, Typography,
} from "@mui/material";
import RefreshIcon from "@mui/icons-material/Refresh";
import ThumbUpOffAltIcon from "@mui/icons-material/ThumbUpOffAlt";
import ThumbDownOffAltIcon from "@mui/icons-material/ThumbDownOffAlt";
import scenarioService from "../../services/ai/scenarioService";
import reduxUtil from "../../utils/redux-util";
import { C } from "@plannel/components/ai/aiTokens";
import { FONT } from "@plannel/components/ai/typographyTokens";

// 개발자/관리자용 Agent Chat 트레이스 뷰어 — z_ai_chat_* 저장분을 세션 → 턴 → 상세로 탐색.
// "이 답변이 왜 나왔나"를 역추적하는 화면이라 SQL 전문·원시 이력·지표를 그대로 노출한다
// (일반 사용자용 챗 패널은 라벨 칩만 노출 — 이 화면은 ROLE_ADMIN 메뉴에만 연결).

// GENERAL = 시나리오 플래닝 밖 화면(홈·업무 화면)의 전역 챗 — module_cd 에 그대로 저장된다.
const MODULES = ["ALL", "BF", "DP", "IP", "RP", "MP", "FP", "GENERAL"];

const fmtTs = (ts) => String(ts || "").replace("T", " ").slice(0, 19);

// BIGINT id·토큰 값은 json-bigint 가 객체로 줄 수 있다 — 표시·비교는 항상 문자열로.
const s = (v) => (v == null ? "" : String(v));

// JSON pretty-print — 응답에 섞인 네이티브 BigInt(json-bigint 파싱 결과)는 JSON.stringify 가
// 직렬화하지 못하고 throw 한다("Do not know how to serialize a BigInt"). 문자열로 강등해 출력.
const safeJson = (obj) => {
  try {
    return JSON.stringify(obj, (_k, v) => (typeof v === "bigint" ? v.toString() : v), 2);
  } catch {
    try { return String(obj); } catch { return ""; }
  }
};

const PANEL_SX = {
  display: "flex", flexDirection: "column", minHeight: 0,
  border: `1px solid ${C.border}`, borderRadius: "10px", bgcolor: C.bg2, overflow: "hidden",
};
const PANEL_HEAD_SX = {
  p: "7px 10px", bgcolor: C.accL, borderBottom: `1px solid ${C.border}`,
  fontSize: FONT.SUB, fontWeight: 700, color: C.text1, flexShrink: 0,
};
const MONO_SX = {
  fontFamily: "monospace", fontSize: 11.5, lineHeight: 1.55, color: C.text1,
  whiteSpace: "pre-wrap", wordBreak: "break-all",
  bgcolor: C.bg3, border: `1px solid ${C.border}`, borderRadius: "6px", p: 1, m: 0,
  overflow: "auto",
};

// 지표 한 조각 — 개발자 도구라 지표명(tier/lane/tok 등)은 기술 용어 그대로 둔다.
const Metric = ({ k, v }) => (
  <Typography component="span" sx={{ fontSize: FONT.BODY, color: C.text2, mr: 1.2 }}>
    <Box component="span" sx={{ color: C.text3 }}>{k} </Box>{s(v) || "-"}
  </Typography>
);

// SQL 결과 미리보기가 "행 dict 배열"인가 — 그러면 JSON 대신 테이블로 렌더한다.
const isRowArray = (p) => Array.isArray(p) && p.length > 0
  && p.every((r) => r && typeof r === "object" && !Array.isArray(r));

const _cellText = (v) => {
  if (v == null) return "";
  if (typeof v === "object") {
    try { return JSON.stringify(v, (_k, x) => (typeof x === "bigint" ? x.toString() : x)); }
    catch { return String(v); }
  }
  return String(v);
};

// 결과 미리보기 테이블 — 컬럼은 행들의 키 합집합(첫 행 순서 우선).
// 흰 배경 + 진한 글자 + 헤더 회색 + 줄무늬 — 색 배경 위 연한 글씨는 안 읽힌다는 피드백 반영.
const PreviewTable = ({ rows }) => {
  const cols = [];
  rows.forEach((r) => Object.keys(r).forEach((k) => { if (!cols.includes(k)) cols.push(k); }));
  return (
    <Box sx={{ overflowX: "auto", bgcolor: C.bg2, borderRadius: "6px",
               border: `1px solid ${C.border}`, borderLeft: `3px solid ${C.acc}` }}>
      <Box component="table" sx={{ borderCollapse: "collapse", width: "100%" }}>
        <Box component="thead">
          <Box component="tr">
            {cols.map((c) => (
              <Box key={c} component="th" sx={{
                textAlign: "left", p: "4px 9px", whiteSpace: "nowrap",
                fontSize: 11, fontWeight: 700, color: C.text1, bgcolor: C.bg3,
                borderBottom: `1.5px solid ${C.border}`,
              }}>{c}</Box>
            ))}
          </Box>
        </Box>
        <Box component="tbody">
          {rows.map((r, ri) => (
            <Box key={ri} component="tr" sx={{ bgcolor: ri % 2 === 1 ? C.bg : "transparent" }}>
              {cols.map((c) => (
                <Box key={c} component="td" sx={{
                  p: "4px 9px", fontSize: 11.5, fontFamily: "monospace", color: C.text1,
                  wordBreak: "break-all", maxWidth: 280, verticalAlign: "top",
                  borderBottom: ri < rows.length - 1 ? `1px solid ${C.border}` : "none",
                }}>{_cellText(r[c])}</Box>
              ))}
            </Box>
          ))}
        </Box>
      </Box>
    </Box>
  );
};

// ── 지연 타임라인 ────────────────────────────────────────────────────────────
// "턴 20초가 어디로 갔나"에 대한 답 자체. 어떤 구간도 칠해지지 않은 갭 = 미계측 배관 시간.
// 트랙을 둘로 나눈 이유: 서브 LLM·SQL 은 도구 실행 **안에서** 일어나 한 줄에 그리면 겹친다.
// stage = LLM·도구가 아닌 턴 준비 구간(프롬프트 조립·Agent 생성·재수화) = other_ms 의 내역
const SEG_COLOR = { round: "#1976d2", sub: "#7b1fa2", sql: "#2e7d32", tool: "#9e9e9e",
                    stage: "#ed6c02", error: "#d32f2f" };

const segKind = (e) => {
  if (e.kind === "llm") return e.role === "sub" ? "sub" : "round";
  if (e.kind === "stage") return "stage";
  if (e.kind === "sql_exec" || e.sql) return "sql";
  return "tool";
};

const Timeline = ({ trace, t }) => {
  const t0 = Number(s(trace?.start_ts)) || 0;
  const totalMs = Number(s(trace?.elapsed_ms)) || 0;
  if (!t0 || totalMs <= 0) return null;

  const segs = (trace.entries || []).map((e, i) => {
    const kind = segKind(e);
    const ms = Number(s(e.elapsed_ms)) || 0;
    const end = (Number(s(e.ts)) - t0) * 1000;
    const start = Math.max(0, end - ms);
    return {
      i, kind, ms,
      left: Math.max(0, Math.min(100, (start / totalMs) * 100)),
      width: Math.max(0.4, Math.min(100, (ms / totalMs) * 100)),
      color: e.status === "error" ? SEG_COLOR.error : SEG_COLOR[kind],
      label: `#${s(e.seq)} ${kind === "round" ? "LLM" : (e.tool || e.label || kind)} · ${Math.round(ms)}ms`,
    };
  }).filter((seg) => Number.isFinite(seg.left) && Number.isFinite(seg.width));

  const Track = ({ kinds }) => (
    <Box sx={{ position: "relative", height: 14, bgcolor: C.bg3, borderRadius: "3px", mb: "3px" }}>
      {segs.filter((seg) => kinds.includes(seg.kind)).map((seg) => (
        <Tooltip key={seg.i} title={seg.label} arrow>
          <Box sx={{
            position: "absolute", top: 0, height: "100%", borderRadius: "3px",
            left: `${seg.left}%`, width: `${seg.width}%`, bgcolor: seg.color, opacity: 0.85,
            "&:hover": { opacity: 1 },
          }} />
        </Tooltip>
      ))}
    </Box>
  );

  const Legend = ({ c, label }) => (
    <Box sx={{ display: "inline-flex", alignItems: "center", gap: "3px", mr: 1.2 }}>
      <Box sx={{ width: 8, height: 8, borderRadius: "2px", bgcolor: c }} />
      <Typography component="span" sx={{ fontSize: FONT.BODY, color: C.text3 }}>{label}</Typography>
    </Box>
  );

  return (
    <Box sx={{ mb: 1.2 }}>
      <Typography sx={{ fontSize: FONT.BODY, color: C.text3, mb: "3px" }}>
        {t("agentTraceTimeline")} · {Math.round(totalMs)}ms
      </Typography>
      <Track kinds={["stage", "round", "tool"]} />
      <Track kinds={["sub", "sql"]} />
      <Box sx={{ mt: "2px" }}>
        <Legend c={SEG_COLOR.round} label={t("agentTraceLegendLlm")} />
        <Legend c={SEG_COLOR.tool} label={t("agentTraceLegendTool")} />
        <Legend c={SEG_COLOR.sub} label={t("agentTraceLegendSubLlm")} />
        <Legend c={SEG_COLOR.sql} label={t("agentTraceLegendSql")} />
        <Legend c={SEG_COLOR.stage} label={t("agentTraceLegendStage")} />
      </Box>
    </Box>
  );
};

const AiChatTraceViewer = ({ viewName }) => {
  const { t } = useTranslation();
  const reduxDispatch = useDispatch();
  const saved = reduxUtil.getViewState(viewName);

  const [moduleFilter, setModuleFilter] = useState(saved?.moduleFilter ?? "ALL");
  const [feedbackOnly, setFeedbackOnly] = useState(saved?.feedbackOnly === true);
  const [includeEval, setIncludeEval] = useState(saved?.includeEval === true);   // 평가 하니스 세션(chat-eval)
  const [sessions, setSessions] = useState(null);      // null=로딩
  const [selSession, setSelSession] = useState(null);  // session_id
  const [turns, setTurns] = useState(null);
  const [selTurnId, setSelTurnId] = useState(null);    // String(id)
  const [detail, setDetail] = useState(null);
  const [detailLoading, setDetailLoading] = useState(false);
  const [detailTab, setDetailTab] = useState(0);

  const loadSessions = useCallback(async (mod, fb, ev) => {
    setSessions(null);
    setSelSession(null); setTurns(null); setSelTurnId(null); setDetail(null);
    try {
      const r = await scenarioService.chatTraceSessions(mod, { feedbackOnly: fb, includeEval: ev });
      setSessions(r?.sessions || []);
    } catch {
      setSessions([]);
    }
  }, []);

  useEffect(() => {
    loadSessions(moduleFilter, feedbackOnly, includeEval);
    reduxDispatch(reduxUtil.updateViewState(
      reduxUtil.createObjectState(viewName, { moduleFilter, feedbackOnly, includeEval })));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [moduleFilter, feedbackOnly, includeEval]);

  const openSession = async (sessionId) => {
    setSelSession(sessionId);
    setTurns(null); setSelTurnId(null); setDetail(null);
    try {
      const r = await scenarioService.chatTraceTurns(moduleFilter, sessionId);
      setTurns(r?.turns || []);
    } catch {
      setTurns([]);
    }
  };

  const openTurn = async (turnId) => {
    const tid = s(turnId);
    setSelTurnId(tid);
    setDetail(null); setDetailTab(0); setDetailLoading(true);
    try {
      const r = await scenarioService.chatTraceTurnDetail(moduleFilter, tid);
      setDetail(r);
    } catch {
      setDetail(null);
    } finally {
      setDetailLoading(false);
    }
  };

  const Empty = ({ msgKey }) => (
    <Typography sx={{ p: 2, fontSize: FONT.BODY, color: C.text3 }}>{t(msgKey)}</Typography>
  );
  const Loading = () => (
    <Box sx={{ p: 2, display: "flex", justifyContent: "center" }}><CircularProgress size={18} /></Box>
  );

  const payload = detail?.assistant_payload || {};
  const trace = detail?.trace || {};
  const traceEntries = trace.entries || [];
  // 요약 지표 — 이 턴의 실행 쿼리 수와 DB 시간 합계(엔진 이벤트 기록 기준)
  const sqlEntries = traceEntries.filter((e) => e && e.sql);
  const dbTotalMs = Math.round(sqlEntries.reduce((a, e) => a + (Number(s(e.elapsed_ms)) || 0), 0));
  // 지연 배분 — 원천은 trace 요약(스칼라 컬럼은 목록 정렬용 사본이라 상세에선 trace 를 본다).
  //   elapsed ≈ llm_ms + tool_ms + other_ms. sql_ms / sub_ms 는 tool_ms 의 내역이므로 더하지 말 것.
  const llm = trace.llm || {};
  const num = (v) => (v == null || v === "" ? null : Math.round(Number(s(v))));
  const cacheHit = llm.cache_hit_pct == null ? null : `${s(llm.cache_hit_pct)}%`;
  // 개발자 도구라 단위는 기술 표기 그대로(tier/lane/tok 와 같은 결) — 번역 대상 아님
  const prefix = trace.prefix
    ? `${s(trace.prefix.system_chars)} ch / ${s(trace.prefix.n_tools)} tools`
    : null;

  return (
    <Box sx={{ height: "calc(100vh - 125px)", display: "flex", flexDirection: "column", p: 1.5, gap: 1 }}>
      {/* 툴바 */}
      <Box sx={{ display: "flex", alignItems: "center", gap: 1.5, flexShrink: 0 }}>
        <FormControl size="small" sx={{ minWidth: 140 }}>
          <InputLabel>{t("agentTraceModule")}</InputLabel>
          <Select value={moduleFilter} label={t("agentTraceModule")} size="small"
            onChange={(e) => setModuleFilter(e.target.value)}>
            {MODULES.map((m) => (
              <MenuItem key={m} value={m}>{m === "ALL" ? t("agentTraceAllModules") : m}</MenuItem>
            ))}
          </Select>
        </FormControl>
        <FormControlLabel
          control={<Checkbox size="small" checked={feedbackOnly}
            onChange={(e) => setFeedbackOnly(e.target.checked)} />}
          label={<Typography sx={{ fontSize: FONT.SUB }}>{t("agentTraceFeedbackOnly")}</Typography>}
        />
        <FormControlLabel
          control={<Checkbox size="small" checked={includeEval}
            onChange={(e) => setIncludeEval(e.target.checked)} />}
          label={<Typography sx={{ fontSize: FONT.SUB }}>{t("agentTraceIncludeEval")}</Typography>}
        />
        <Tooltip title={t("agentTraceRefresh")}>
          <IconButton size="small" onClick={() => loadSessions(moduleFilter, feedbackOnly, includeEval)}>
            <RefreshIcon sx={{ fontSize: 17 }} />
          </IconButton>
        </Tooltip>
      </Box>

      {/* 3열 — 세션 / 턴 / 상세 */}
      <Box sx={{ flex: 1, minHeight: 0, display: "flex", gap: 1 }}>
        {/* 세션 목록 */}
        <Box sx={{ ...PANEL_SX, width: 300, flexShrink: 0 }}>
          <Box sx={PANEL_HEAD_SX}>{t("agentTraceSessionsTitle")}</Box>
          <Box sx={{ flex: 1, overflowY: "auto" }}>
            {sessions == null ? <Loading />
              : sessions.length === 0 ? <Empty msgKey="agentTraceNoData" />
              : sessions.map((sess) => (
                <Box key={sess.session_id} onClick={() => openSession(sess.session_id)}
                  sx={{
                    p: "7px 10px", cursor: "pointer", borderBottom: `1px solid ${C.border}`,
                    bgcolor: selSession === sess.session_id ? C.accL : "transparent",
                    "&:hover": { bgcolor: C.accL },
                  }}>
                  <Typography noWrap sx={{ fontSize: FONT.SUB, color: C.text1 }}>
                    {sess.title || t("agentChatUntitled")}
                  </Typography>
                  <Box sx={{ display: "flex", alignItems: "center", gap: 0.5, mt: "2px", flexWrap: "wrap" }}>
                    <Chip label={sess.module_cd} size="small"
                      sx={{ height: 15, fontSize: 9, bgcolor: C.bg3 }} />
                    <Typography sx={{ fontSize: FONT.BODY, color: C.text3 }} noWrap>
                      {sess.user_id} · {s(sess.turn_count)}{t("agentTraceTurnUnit")}
                    </Typography>
                    {Number(s(sess.down_count)) > 0 && (
                      <Box sx={{ display: "inline-flex", alignItems: "center", gap: "1px", color: "#d32f2f" }}>
                        <ThumbDownOffAltIcon sx={{ fontSize: 11 }} />
                        <Typography sx={{ fontSize: FONT.BODY }}>{s(sess.down_count)}</Typography>
                      </Box>
                    )}
                    {sess.active_flg === "N" && (
                      <Chip label={t("agentTraceDeleted")} size="small"
                        sx={{ height: 15, fontSize: 9, bgcolor: "#fdecea", color: "#d32f2f" }} />
                    )}
                    {sess.user_id === "chat-eval" && (
                      <Chip label={t("agentTraceEvalChip")} size="small"
                        sx={{ height: 15, fontSize: 9, bgcolor: "#e8eaf6", color: "#3949ab" }} />
                    )}
                  </Box>
                  <Typography sx={{ fontSize: FONT.BODY, color: C.text3 }}>{fmtTs(sess.last_ts)}</Typography>
                </Box>
              ))}
          </Box>
        </Box>

        {/* 턴 목록 */}
        <Box sx={{ ...PANEL_SX, width: 330, flexShrink: 0 }}>
          <Box sx={PANEL_HEAD_SX}>{t("agentTraceTurnsTitle")}</Box>
          <Box sx={{ flex: 1, overflowY: "auto" }}>
            {selSession == null ? <Empty msgKey="agentTraceSelectSession" />
              : turns == null ? <Loading />
              : turns.length === 0 ? <Empty msgKey="agentTraceNoData" />
              : turns.map((turn) => (
                <Box key={s(turn.id)} onClick={() => openTurn(turn.id)}
                  sx={{
                    p: "7px 10px", cursor: "pointer", borderBottom: `1px solid ${C.border}`,
                    bgcolor: selTurnId === s(turn.id) ? C.accL : "transparent",
                    "&:hover": { bgcolor: C.accL },
                  }}>
                  <Typography noWrap sx={{ fontSize: FONT.SUB, color: C.text1 }}>
                    #{s(turn.seq)} {turn.user_message}
                  </Typography>
                  <Box sx={{ mt: "2px", display: "flex", alignItems: "center", flexWrap: "wrap" }}>
                    <Metric k="tier" v={turn.tier} />
                    <Metric k="lane" v={turn.lane} />
                    <Metric k="tok" v={`${s(turn.in_tokens)}/${s(turn.out_tokens)}`} />
                    <Metric k="ms" v={turn.elapsed_ms} />
                    <Metric k="tools" v={turn.n_tools} />
                    {/* 지연 배분 — LLM 이 턴의 60% 를 넘으면 붉게. "이 턴은 LLM 바운드"를 목록에서 판별 */}
                    {turn.llm_ms != null && (
                      <Box component="span" sx={{
                        mr: 1.2, px: "4px", borderRadius: "3px", fontSize: FONT.BODY,
                        color: Number(s(turn.llm_ms)) > 0.6 * (Number(s(turn.elapsed_ms)) || 1)
                          ? "#d32f2f" : C.text2,
                        bgcolor: Number(s(turn.llm_ms)) > 0.6 * (Number(s(turn.elapsed_ms)) || 1)
                          ? "#fdecea" : "transparent",
                      }}>
                        <Box component="span" sx={{ color: C.text3 }}>llm </Box>{s(turn.llm_ms)}
                      </Box>
                    )}
                    {turn.llm_rounds != null && <Metric k="R" v={turn.llm_rounds} />}
                    {turn.ttfb_ms != null && <Metric k="ttfb" v={turn.ttfb_ms} />}
                    {turn.escalated_flg === "Y" && (
                      <Chip label="escalated" size="small" sx={{ height: 15, fontSize: 9 }} />
                    )}
                    {turn.truncated_flg === "Y" && (
                      <Chip label="truncated" size="small" sx={{ height: 15, fontSize: 9 }} />
                    )}
                    {String(turn.feedback || "").includes("down") && (
                      <ThumbDownOffAltIcon sx={{ fontSize: 12, color: "#d32f2f", ml: 0.3 }} />
                    )}
                    {String(turn.feedback || "").includes("up") && (
                      <ThumbUpOffAltIcon sx={{ fontSize: 12, color: C.acc, ml: 0.3 }} />
                    )}
                  </Box>
                </Box>
              ))}
          </Box>
        </Box>

        {/* 턴 상세 */}
        <Box sx={{ ...PANEL_SX, flex: 1, minWidth: 0 }}>
          <Box sx={{ ...PANEL_HEAD_SX, display: "flex", alignItems: "center", gap: 1 }}>
            {t("agentTraceDetailTitle")}
            {detail && (
              <Typography sx={{ fontSize: FONT.BODY, color: C.text3, fontWeight: 400 }} noWrap>
                {detail.module_cd} · {detail.user_id} · seq {s(detail.seq)} · {fmtTs(detail.created_ts)}
              </Typography>
            )}
          </Box>
          {selTurnId == null ? <Empty msgKey="agentTraceSelectTurn" />
            : detailLoading ? <Loading />
            : detail == null ? <Empty msgKey="agentTraceLoadFail" />
            : (
            <>
              <Tabs value={detailTab} onChange={(_, v) => setDetailTab(v)}
                sx={{ minHeight: 30, borderBottom: `1px solid ${C.border}`, flexShrink: 0,
                      "& .MuiTab-root": { minHeight: 30, py: 0, fontSize: FONT.SUB, textTransform: "none" } }}>
                <Tab label={t("agentTraceTabSummary")} />
                <Tab label={`${t("agentTraceTabTrace")} (${traceEntries.length})`} />
                <Tab label={t("agentTraceTabRaw")} />
                <Tab label={`${t("agentTraceTabFeedback")} (${(detail.feedbacks || []).length})`} />
              </Tabs>
              <Box sx={{ flex: 1, overflowY: "auto", p: 1.2 }}>
                {detailTab === 0 && (
                  <Box>
                    {/* 모델은 ID 가 길어 전용 줄 — 나머지 지표는 라벨 그리드로 */}
                    <Box sx={{ mb: 0.3 }}>
                      <Metric k={t("agentTraceMetricModel")} v={detail.model_id} />
                    </Box>
                    <Box sx={{ mb: 1, display: "grid", rowGap: "2px", columnGap: "12px",
                               gridTemplateColumns: "repeat(auto-fill, minmax(170px, 1fr))" }}>
                      <Metric k={t("agentTraceMetricTier")} v={detail.tier} />
                      <Metric k={t("agentTraceMetricLane")} v={detail.lane} />
                      <Metric k={t("agentTraceMetricEscalated")} v={detail.escalated_flg} />
                      <Metric k={t("agentTraceMetricInTokens")} v={detail.in_tokens} />
                      <Metric k={t("agentTraceMetricOutTokens")} v={detail.out_tokens} />
                      <Metric k={t("agentTraceMetricTools")} v={detail.n_tools} />
                      <Metric k={t("agentTraceMetricElapsed")} v={detail.elapsed_ms} />
                      <Metric k={t("agentTraceMetricSqlCount")} v={sqlEntries.length} />
                      <Metric k={t("agentTraceMetricDbMs")} v={dbTotalMs} />
                      <Metric k={t("agentTraceMetricStop")} v={detail.stop_reason} />
                      <Metric k={t("agentTraceMetricTruncated")} v={detail.truncated_flg} />
                    </Box>
                    {/* ── 지연 배분 ── in/out tokens 는 최종 응답분이라 턴 전체와 다르다(에스컬레이션) */}
                    <Box sx={{ mb: 1, display: "grid", rowGap: "2px", columnGap: "12px",
                               gridTemplateColumns: "repeat(auto-fill, minmax(170px, 1fr))" }}>
                      <Metric k={t("agentTraceMetricLlmMs")} v={num(llm.llm_ms)} />
                      <Metric k={t("agentTraceMetricLlmRounds")} v={llm.rounds} />
                      <Metric k={t("agentTraceMetricTtfb")} v={num(trace.ttfb_ms)} />
                      <Metric k={t("agentTraceMetricSubLlm")}
                        v={llm.sub_calls ? `${num(llm.sub_ms)} (${s(llm.sub_calls)})` : null} />
                      <Metric k={t("agentTraceMetricToolMs")} v={num(trace.tool_ms)} />
                      <Metric k={t("agentTraceMetricSqlMs")} v={num(trace.sql_ms)} />
                      <Metric k={t("agentTraceMetricOtherMs")} v={num(trace.other_ms)} />
                      <Metric k={t("agentTraceMetricCacheTokens")}
                        v={`${s(llm.cache_read)}/${s(llm.cache_write)}`} />
                      <Metric k={t("agentTraceMetricCacheHit")} v={cacheHit} />
                      <Metric k={t("agentTraceMetricTurnTokens")}
                        v={`${s(llm.in)}/${s(llm.out)}`} />
                      <Metric k={t("agentTraceMetricPrefix")} v={prefix} />
                    </Box>
                    <Timeline trace={trace} t={t} />
                    <Typography sx={{ fontSize: FONT.BODY, color: C.text3, mb: 0.3 }}>
                      {t("agentTraceUserMessage")}
                    </Typography>
                    <Box component="pre" sx={{ ...MONO_SX, mb: 1 }}>{detail.user_message}</Box>
                    <Typography sx={{ fontSize: FONT.BODY, color: C.text3, mb: 0.3 }}>
                      {t("agentTraceReply")}
                    </Typography>
                    <Box component="pre" sx={MONO_SX}>{payload.reply || ""}</Box>
                    {detail.error_msg && (
                      <Box component="pre" sx={{ ...MONO_SX, mt: 1, color: "#d32f2f" }}>{detail.error_msg}</Box>
                    )}
                  </Box>
                )}
                {detailTab === 1 && (
                  traceEntries.length === 0 ? <Empty msgKey="agentTraceNoTools" />
                  : <>
                    <Timeline trace={trace} t={t} />
                    {traceEntries.map((e, i) => (
                    <Box key={i} sx={{ mb: 1.2, border: `1px solid ${C.border}`, borderRadius: "8px", p: 1,
                                       borderLeft: `3px solid ${e.status === "error" ? SEG_COLOR.error : SEG_COLOR[segKind(e)]}` }}>
                      <Box sx={{ display: "flex", alignItems: "center", flexWrap: "wrap", mb: 0.5 }}>
                        {/* SQL 은 실행 주체 도구에 귀속 표기 — "SQL · get_xxx" (도구 밖 배관 쿼리만 "SQL")
                            LLM 은 메인 라운드(round N)와 도구 안의 서브 호출(nl2sql 등)을 구분한다 */}
                        <Typography sx={{ fontSize: FONT.SUB, fontWeight: 700, color: C.text1, mr: 1 }}>
                          #{s(e.seq)} {e.kind === "llm"
                            ? (e.role === "sub"
                                ? `LLM · ${e.label || "sub"}${e.tool ? ` · ${e.tool}` : ""}`
                                : `LLM round${Number(s(e.attempt)) > 1 ? ` (attempt ${s(e.attempt)})` : ""}`)
                            : e.kind === "stage"
                            ? `stage · ${e.tool}`
                            : e.sql
                            ? (e.tool && e.tool !== "sql" ? `SQL · ${e.tool}` : "SQL")
                            : e.tool}
                        </Typography>
                        <Chip label={e.status || "ok"} size="small"
                          sx={{ height: 15, fontSize: 9, mr: 1,
                                bgcolor: e.status === "error" ? "#fdecea" : C.bg3,
                                color: e.status === "error" ? "#d32f2f" : C.text2 }} />
                        <Metric k="ms" v={e.elapsed_ms} />
                        {/* latency_ms(Bedrock 서버측) vs elapsed_ms(우리측) — 차이가 네트워크·스트림 소비 */}
                        {e.kind === "llm" && e.latency_ms != null && <Metric k="srv" v={e.latency_ms} />}
                        {e.kind === "llm" && (
                          <Metric k="tok" v={`${s(e.in_tokens)}/${s(e.out_tokens)}`} />
                        )}
                        {e.kind === "llm" && (e.cache_read != null || e.cache_write != null) && (
                          <Metric k="cache r/w" v={`${s(e.cache_read)}/${s(e.cache_write)}`} />
                        )}
                        {Number(s(e.row_count)) >= 0 && <Metric k="rows" v={e.row_count} />}
                        {e.kind !== "llm" && e.label && <Metric k="label" v={e.label} />}
                      </Box>
                      {/* 도구 호출 인자 */}
                      {!e.sql && e.input && (
                        <Box component="pre" sx={{ ...MONO_SX, mb: 0.5 }}>{`args: ${e.input}`}</Box>
                      )}
                      {e.sql && (
                        <>
                          <Typography sx={{ fontSize: FONT.BODY, color: C.text2, fontWeight: 700, mb: "2px" }}>
                            {t("agentTraceQueryLabel")}
                          </Typography>
                          <Box component="pre" sx={MONO_SX}>{e.sql}</Box>
                        </>
                      )}
                      {e.error && (
                        <Box component="pre" sx={{ ...MONO_SX, mt: 0.5, color: "#d32f2f" }}>{e.error}</Box>
                      )}
                      {/* 결과 미리보기 — SQL 행 배열은 테이블로(JSON 은 읽기 어렵다는 피드백),
                          도구 반환 텍스트·단일 객체는 기존 블록으로 렌더 */}
                      {e.result_preview != null && (
                        <>
                          <Typography sx={{ fontSize: FONT.BODY, color: C.text2, fontWeight: 700, mt: 0.5, mb: "2px" }}>
                            {t("agentTraceResultLabel")}
                          </Typography>
                          {isRowArray(e.result_preview) ? (
                            <PreviewTable rows={e.result_preview} />
                          ) : (
                            <Box component="pre" sx={{
                              ...MONO_SX, maxHeight: 180,
                              bgcolor: C.bg2, borderLeft: `3px solid ${C.acc}`,
                            }}>
                              {typeof e.result_preview === "string" ? e.result_preview : safeJson(e.result_preview)}
                            </Box>
                          )}
                        </>
                      )}
                    </Box>
                    ))}
                  </>
                )}
                {detailTab === 2 && (
                  <Box component="pre" sx={MONO_SX}>
                    {safeJson(detail.raw_messages)}
                  </Box>
                )}
                {detailTab === 3 && (
                  (detail.feedbacks || []).length === 0 ? <Empty msgKey="agentTraceNoData" />
                  : detail.feedbacks.map((f, i) => (
                    <Box key={i} sx={{ mb: 1, border: `1px solid ${C.border}`, borderRadius: "8px", p: 1 }}>
                      <Box sx={{ display: "flex", alignItems: "center", gap: 0.5 }}>
                        {f.rating === "down"
                          ? <ThumbDownOffAltIcon sx={{ fontSize: 13, color: "#d32f2f" }} />
                          : <ThumbUpOffAltIcon sx={{ fontSize: 13, color: C.acc }} />}
                        <Typography sx={{ fontSize: FONT.SUB, color: C.text1 }}>{f.user_id}</Typography>
                        <Typography sx={{ fontSize: FONT.BODY, color: C.text3 }}>{fmtTs(f.created_ts)}</Typography>
                      </Box>
                      {f.reason && (
                        <Typography sx={{ fontSize: FONT.SUB, color: C.text2, mt: 0.3 }}>{f.reason}</Typography>
                      )}
                    </Box>
                  ))
                )}
              </Box>
            </>
          )}
        </Box>
      </Box>
    </Box>
  );
};

export default AiChatTraceViewer;
